import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import Users_no_row from './users_row';
import Pagination from '../../ui/pagination';
import { userService } from "../../../_services";
import Edit_user_pop from './EditUserDialog.jsx';
import Refferral_pop from './ReferralManagementDialog.jsx';
import ChangePass from '../Admin_Common/ChangePasswordDialog.jsx';
import AddUser from './AddUserDialog.jsx';
import DeactivateRefferalLinks from '../Refferal_Link/DeactivateReferralLinksDialog.jsx';
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Search, UserPlus, Loader2, X } from "lucide-react";
import { ConfirmDialog } from "../../ui/confirm-dialog";

/**
 * Users Management Page
 * Modernized with shadcn components and debounced search
 */
function User_Page() {
    const location = useLocation();
    const searchInputRef = useRef(null);
    const debouncedSearchRef = useRef(null);

    // State management
    const [allUserData, setAllUserData] = useState([]);
    const [userData, setUserData] = useState([]);
    const [pageOfItems, setPageOfItems] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [activeTab, setActiveTab] = useState('all');
    const [activeSetting, setActiveSetting] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    
    // Dialog states
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [showReferralPopup, setShowReferralPopup] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showDeactivateLinks, setShowDeactivateLinks] = useState(false);
    const [showAddUser, setShowAddUser] = useState(false);
    const [showInactivePopup, setShowInactivePopup] = useState(false);
    
    // Selected user states
    const [selectedUserFullname, setSelectedUserFullname] = useState('');
    const [selectedRepId, setSelectedRepId] = useState('');
    const [selectedUsername, setSelectedUsername] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');

    // Initialize debounced search
    useEffect(() => {
        debouncedSearchRef.current = debounce((searchText) => {
            performSearch(searchText);
        }, 300);

        return () => {
            if (debouncedSearchRef.current) {
                debouncedSearchRef.current.cancel();
            }
        };
    }, [allUserData, activeTab]);

    // Fetch users on mount
    useEffect(() => {
        fetchUsers();

        // Handle location state for search
        if (location.state?.property_id) {
            setTimeout(() => {
                setSearchInput(location.state.property_id);
                performSearch(location.state.property_id);
            }, 500);
        }
    }, []);

    // Apply tab filter when tab changes
    useEffect(() => {
        performSearch(searchInput);
    }, [activeTab]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');
            
            const apidata = await userService.GetUserList(userId, userName, '-1');
            
            if (apidata && apidata.length > 0) {
                setAllUserData(apidata);
                setUserData(apidata);
            } else {
                setAllUserData([]);
                setUserData([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setAllUserData([]);
            setUserData([]);
        } finally {
            setLoading(false);
        }
    };

    const performSearch = (searchText) => {
        setIsSearching(true);
        
        let filtered = [...allUserData];
        
        // Apply tab filter (matching old handletab logic)
        if (activeTab !== 'all') {
            filtered = filtered.filter(user => {
                const userType = user.userType || '';
                // Match userType with tab name (case insensitive)
                return userType.toLowerCase().includes(activeTab.toLowerCase());
            });
        }
        
        // Apply search filter
        if (searchText && searchText.trim()) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter((user) => {
                const userName = user.userName?.toLowerCase() || '';
                const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
                const email = user.email?.toLowerCase() || '';
                const groupNumber = user.groupNumber?.toLowerCase() || '';

                return (
                    userName.includes(search) ||
                    fullName.includes(search) ||
                    email.includes(search) ||
                    groupNumber.includes(search)
                );
            });
        }
        
        setUserData(filtered.length > 0 ? filtered : [{}]);
        setIsSearching(false);
    };

    const handleSearchChange = (value) => {
        setSearchInput(value);
        setIsSearching(true);
        if (debouncedSearchRef.current) {
            debouncedSearchRef.current(value);
        }
    };

    const clearSearch = () => {
        setSearchInput('');
        performSearch('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        // Clear search when changing tabs
        if (searchInput) {
            setSearchInput('');
        }
    };

    const handleEditUser = (event) => {
        event.stopPropagation();
        const userFullname = event.currentTarget.getAttribute('data-user-fullname');
        const repid = event.currentTarget.getAttribute('data-repid');
        setSelectedUserFullname(userFullname);
        setSelectedRepId(repid);
        setShowEditPopup(true);
    };

    const closeEditUser = () => {
        setShowEditPopup(false);
        refreshUserList();
    };

    const handleReferrals = (event) => {
        event.stopPropagation();
        const userFullname = event.currentTarget.getAttribute('data-user-fullname');
        const numbering = event.currentTarget.getAttribute('data-numbering');
        setSelectedUserFullname(userFullname);
        setSelectedRepId(numbering);
        setShowReferralPopup(true);
    };

    const handleChangePassword = (event) => {
        event.stopPropagation();
        const indexing = event.currentTarget.getAttribute('data-indexing');
        setSelectedRepId(indexing);
        setShowChangePassword(true);
    };

    const closeChangePassword = () => {
        setShowChangePassword(false);
        refreshUserList();
    };

    const handleDeactivateLinks = (event) => {
        event.stopPropagation();
        const uname = event.currentTarget.getAttribute('data-name-user');
        const uid = event.currentTarget.getAttribute('data-numbering');
        setSelectedUsername(uname);
        setSelectedUserId(uid);
        setShowDeactivateLinks(!showDeactivateLinks);
    };

    const handleActivateDeactivate = async (event) => {
        const status = event.currentTarget.getAttribute('data-status');
        const repid = event.currentTarget.getAttribute('data-numbering');
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');

        try {
            await userService.UpdateUserStatus(userId, userName, repid, status);
            refreshUserList();
        } catch (error) {
            console.error('Error updating user status:', error);
        }
    };

    const handleResendActivation = async (event) => {
        try {
            const userId = event.currentTarget.getAttribute('data-numbering');
            const userName = event.currentTarget.getAttribute('data-name-user');
            await userService.ResendAccountActivationEmail(userId, userName);
        } catch (error) {
            console.error('Error resending activation:', error);
        }
    };

    const handleTierCommission = async (event) => {
        const userName = event.currentTarget.getAttribute('data-name-user');
        const userid = event.currentTarget.getAttribute('data-numbering');
        const useremail = event.currentTarget.getAttribute('data-email-user');
        const tiercommSetting = event.currentTarget.getAttribute('data-value');
        const tiercomm = tiercommSetting !== "true";

        try {
            await userService.UpdateTierCommissionSetting(userid, userName, useremail, tiercomm);
            setTimeout(() => refreshUserList(), 2000);
        } catch (error) {
            console.error('Error updating tier commission:', error);
        }
    };

    const handleSecondaryCard = async (event) => {
        const userName = event.currentTarget.getAttribute('data-name-user');
        const userid = event.currentTarget.getAttribute('data-numbering');
        const useremail = event.currentTarget.getAttribute('data-email-user');
        const seccardsetting = event.currentTarget.getAttribute('data-value');
        const seccard = seccardsetting !== "true";

        try {
            await userService.UpdateSecondarySetting(userid, userName, useremail, seccard);
            setTimeout(() => refreshUserList(), 2000);
        } catch (error) {
            console.error('Error updating secondary card:', error);
        }
    };

    const refreshUserList = () => {
        fetchUsers();
    };

    const onChangePage = (pageOfItems) => {
        setPageOfItems(pageOfItems);
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate replace to="/signup" />;
    }

    if (window.atob(localStorage.getItem('UserRole')) !== "Admin") {
        return <Navigate replace to="/page-not-found" />;
    }

        localStorage.setItem('pagename', 'userspage');

    const dataToDisplay = searchInput.length ? userData : pageOfItems;

    return (
        <div className="w-full space-y-6">
            {/* Heading */}
            <h1 className="text-2xl font-semibold text-brand-dark">Users</h1>
            
            {/* Search, Tabs, and Add Button Row */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                {/* Search */}
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-dark/50" />
                    <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search users..."
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        disabled={loading || !allUserData.length}
                        className="pl-10 pr-10"
                    />
                    
                    {/* Loading indicator */}
                    {isSearching && (
                        <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                    )}
                    
                    {/* Clear button */}
                    {searchInput && !isSearching && (
                        <button
                            onClick={clearSearch}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                            aria-label="Clear search"
                            type="button"
                        >
                            <X className="h-4 w-4 text-gray-500" />
                        </button>
                    )}
                </div>

                {/* Tabs and Button */}
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 md:flex-none">
                        <TabsList className="grid grid-cols-3">
                            <TabsTrigger 
                                value="all" 
                                disabled={loading || !allUserData.length}
                            >
                                All
                            </TabsTrigger>
                            <TabsTrigger 
                                value="distributor" 
                                disabled={loading || !allUserData.length}
                            >
                                Distributor
                            </TabsTrigger>
                            <TabsTrigger 
                                value="consumer" 
                                disabled={loading || !allUserData.length}
                            >
                                Consumer
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    <Button 
                        onClick={() => setShowAddUser(true)}
                        disabled={loading}
                        variant="secondary"
                    >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Add Users
                    </Button>
                </div>
            </div>
            
            {/* Card with Table only */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
                                </div>
                    ) : (
                        <>
                            <div className="border-b border-gray-100">
                                <Table>
                                    <TableHeader className="bg-brand-light-1">
                                        <TableRow>
                                            <TableHead className="text-brand-dark">User Name</TableHead>
                                            <TableHead className="text-brand-dark">Full Name</TableHead>
                                            <TableHead className="text-brand-dark">Email</TableHead>
                                            <TableHead className="text-brand-dark">Primary Group Number</TableHead>
                                            <TableHead className="text-brand-dark">Status</TableHead>
                                            <TableHead className="text-brand-dark">Approve Payment</TableHead>
                                            <TableHead className="text-brand-dark text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {dataToDisplay.map((userRow, index) => (
                                                <Users_no_row
                                                key={userRow.userId || index}
                                                    index={index}
                                                {...userRow}
                                                toggle_add_edit_popup={handleEditUser}
                                                toggle_refferal_pop={handleReferrals}
                                                ClosePopUp={handleChangePassword}
                                                activate_deactivate={handleActivateDeactivate}
                                                deactivate_refferals_links={handleDeactivateLinks}
                                                setStateOfParent={(apidata, tabname) => {
                                                    setUserData(apidata);
                                                    setAllUserData(apidata);
                                                    handleTabChange(tabname || 'all');
                                                }}
                                                show_settings={activeSetting}
                                                setActiveSetting={setActiveSetting}
                                                resendActivationApi={handleResendActivation}
                                                TierCommission={handleTierCommission}
                                                SecondaryCard={handleSecondaryCard}
                                                tabName={activeTab}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            <div className="px-4 py-4">
                                <Pagination 
                                    items={userData} 
                                    onChangePage={onChangePage} 
                                    showInfo={true}
                                />
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Modals */}
            {showInactivePopup && (
                <ConfirmDialog
                    open={showInactivePopup}
                    onOpenChange={setShowInactivePopup}
                    title="Inactive User"
                    description="This user needs to verify their email address before their account can be activated."
                    onConfirm={() => setShowInactivePopup(false)}
                    confirmText="Ok"
                    showIcon={false}
                />
            )}

            {showEditPopup && (
                                <div className="asign_edit_pop edit_user_popup">
                    <Edit_user_pop 
                        title={`Edit ${selectedUserFullname}`} 
                        repid={selectedRepId} 
                        toggle_add_edit_popup={closeEditUser} 
                    />
                                </div>
            )}

            {showReferralPopup && (
                                <div className="asign_edit_pop edit_user_popup">
                    <Refferral_pop 
                        refferal_title={`${selectedUserFullname} Referrals`} 
                        toggle_refferal_pop={() => setShowReferralPopup(false)} 
                        repid={selectedRepId} 
                    />
                                </div>
            )}

            {showChangePassword && (
                                <div className="reset_pass_box changepassword_box">
                    <ChangePass 
                        ClosePopUp={closeChangePassword} 
                        repid={selectedRepId} 
                    />
                </div>
            )}

            {showDeactivateLinks && (
                                <div className="asign_edit_pop edit_user_popup">
                    <DeactivateRefferalLinks 
                        uname={selectedUsername} 
                        uid={selectedUserId} 
                        deactivate_refferals_links={handleDeactivateLinks} 
                    />
                                </div>
            )}

            {showAddUser && (
                <AddUser 
                    open={showAddUser}
                    onOpenChange={setShowAddUser}
                    onSuccess={() => {
                        setShowAddUser(false);
                        refreshUserList();
                    }}
                />
            )}
                        </div>
    );
    }

export default User_Page;
